import { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus, Upload, Download } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { fetchJson, uploadFile, type Customer } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import { useConfirm } from '@/components/ConfirmDialog';

const VIA_LABELS: Record<Customer['createdVia'], string> = {
  ivr: 'IVR',
  import: 'ייבוא',
  manual: 'ידני'
};

function CustomerForm({
  initial,
  onSave,
  onCancel
}: {
  initial?: Partial<Customer>;
  onSave: (data: { phone: string; name?: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [phone, setPhone] = useState(initial?.phone || '');
  const [name, setName] = useState(initial?.name || '');
  const [busy, setBusy] = useState(false);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          await onSave({ phone, name });
        } finally {
          setBusy(false);
        }
      }}
      className="space-y-4"
    >
      <div className="space-y-2">
        <Label htmlFor="phone">טלפון *</Label>
        <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} required dir="ltr" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="name">שם (אופציונלי)</Label>
        <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          ביטול
        </Button>
        <Button type="submit" disabled={busy}>
          {busy ? 'שומר...' : 'שמור'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function Customers() {
  const confirm = useConfirm();
  const [items, setItems] = useState<Customer[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchJson<{ items: Customer[]; total: number }>('/api/customers?all=1');
      setItems(res.items);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter(
      (c) =>
        c.phone.toLowerCase().includes(term) || (c.name || '').toLowerCase().includes(term)
    );
  }, [items, q]);

  const columns: DataTableColumn<Customer>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'שם',
        cell: (c) => c.name || <span className="text-muted-foreground">-</span>,
        exportValue: (c) => c.name || ''
      },
      {
        key: 'phone',
        header: 'טלפון',
        cell: (c) => (
          <span className="font-mono text-xs" dir="ltr">
            {c.phone}
          </span>
        ),
        exportValue: (c) => c.phone
      },
      {
        key: 'createdVia',
        header: 'מקור',
        cell: (c) => <Badge variant="outline">{VIA_LABELS[c.createdVia]}</Badge>,
        exportValue: (c) => VIA_LABELS[c.createdVia]
      },
      {
        key: 'createdAt',
        header: 'נוצר בתאריך',
        cell: (c) => <span className="text-sm">{formatDateTime(c.createdAt)}</span>,
        exportValue: (c) => formatDateTime(c.createdAt)
      },
      {
        key: 'actions',
        header: 'פעולות',
        align: 'end',
        exportable: false,
        stopClickPropagation: true,
        cell: (c) => (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="icon" onClick={() => setEditing(c)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={async () => {
                const ok = await confirm({
                  title: `למחוק את הלקוח "${c.name || c.phone}"?`,
                  description: 'כל הרשומות שלו יימחקו. פעולה זו לא ניתנת לביטול.',
                  confirmText: 'מחק',
                  variant: 'destructive'
                });
                if (!ok) return;
                await fetchJson(`/api/customers/${c.id}`, { method: 'DELETE' });
                toast.success('נמחק');
                refresh();
              }}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        )
      }
    ],
    [confirm, refresh]
  );

  return (
    <div className="page-fill flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">לקוחות</h1>
          <p className="text-sm text-muted-foreground">{items.length} לקוחות בסך הכל</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm">
            <a href="/api/customers/template.xlsx" download>
              <Download className="h-4 w-4" />
              הורד תבנית
            </a>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setImporting(true)}>
            <Upload className="h-4 w-4" />
            ייבא מ-Excel
          </Button>
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />
            הוסף לקוח
          </Button>
        </div>
      </div>

      <Card className="flex min-h-0 flex-1 flex-col">
        <CardHeader>
          <Input
            placeholder="חיפוש לפי טלפון או שם..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-md"
          />
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col">
          <DataTable
            columns={columns}
            data={filtered}
            getRowKey={(c) => c.id}
            loading={loading}
            emptyMessage="לא נמצאו לקוחות."
            exportFileName="לקוחות"
            exportSheetName="לקוחות"
            pagination
          />
        </CardContent>
      </Card>

      <Dialog open={creating || !!editing} onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'עריכת לקוח' : 'הוספת לקוח'}</DialogTitle>
          </DialogHeader>
          <CustomerForm
            initial={editing || undefined}
            onCancel={() => {
              setCreating(false);
              setEditing(null);
            }}
            onSave={async (data) => {
              try {
                if (editing) {
                  await fetchJson(`/api/customers/${editing.id}`, {
                    method: 'PATCH',
                    body: JSON.stringify(data)
                  });
                  toast.success('עודכן');
                } else {
                  await fetchJson('/api/customers', {
                    method: 'POST',
                    body: JSON.stringify(data)
                  });
                  toast.success('נוצר');
                }
                setCreating(false);
                setEditing(null);
                refresh();
              } catch (err: unknown) {
                const e = err as { message?: string };
                toast.error(e.message === 'phone_exists' ? 'הטלפון כבר קיים' : (e.message || 'שגיאה'));
              }
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={importing} onOpenChange={setImporting}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ייבוא לקוחות מ-Excel</DialogTitle>
            <DialogDescription>
              העלה קובץ עם עמודות phone (חובה) ו-name (אופציונלי). הורד את התבנית להתחלה.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setImporting(false)}>
                ביטול
              </Button>
              <Button
                disabled={!importFile}
                onClick={async () => {
                  if (!importFile) return;
                  try {
                    const res = await uploadFile<{
                      added: number;
                      updated: number;
                      skipped: number;
                      errors: { row: number; phone: string; error: string }[];
                    }>('/api/customers/import', importFile);
                    toast.success(`נוספו ${res.added}, עודכנו ${res.updated}, דילוג ${res.skipped}, שגיאות ${res.errors.length}`);
                    setImporting(false);
                    setImportFile(null);
                    refresh();
                  } catch (err: unknown) {
                    const e = err as { message?: string };
                    toast.error(e.message || 'שגיאה');
                  }
                }}
              >
                ייבא
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
